import React from "react";
import {
  FormInput,
  Card,
  CardBody,
  FormGroup,
  Button,
} from "../../components/ui";
import { Link, matchPath } from "react-router-dom";
import { withRouter } from "../../hooks/withRouter";
import SharePopover from "../../components/common/SharePopover/SharePopover";
import ToolbarSelect from "../../components/common/ToolbarSelect/ToolbarSelect";
import JoditEditor from "jodit-react";
import {
  getTradingUnit,
  createAlert,
  createFavoriteAlert,
  updateAlert,
  setrackingAlert,
  removeFavoriteAlert,
  getMemberAlerts,
  getWistiaConfig,
  createAlertUpdate,
  getMixedAlerts,
  getAlert,
} from "../../api";
import "react-image-gallery/styles/css/image-gallery.css";
/* Publish form styles + feed chrome (Lovable). */
import "../AlertsPublish.scss";
import "./Alerts.feed.scss";
import ImageGallery from "react-image-gallery";
import { formatToLocalDate, formatRelativeTime, getPublisherAvatarUrl } from "../../utils";
import { handleApiError } from "../../utils/apiErrorHandler";
import { showLovableToast } from "../../components/common/LovableToast/showLovableToast";
import AlertsFeedSkeleton from "./AlertsFeedSkeleton";
import FilterEmptyState from "../../components/common/FilterEmptyState/FilterEmptyState";
import NotificationService from "../../components/alerts";
import publisherStore from "../../flux/publisherStore";
import { getPublishers } from "../../flux/publisherAction";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const editorConfig = {
  uploader: {
    url: `${process.env.REACT_APP_API_URL}media/image`,
    isSuccess: function (resp) {
      return true;
    },
    process: function (resp) {
      return {
        files: [resp.url],
        path: resp.url,
        baseurl: resp.url,
        error: false,
        message: "",
      };
    },
    defaultHandlerSuccess: function (data) {
      const editor = this.jodit || this;
      editor.selection.insertImage(data.baseurl);
    },
  },
  toolbarAdaptive: false,
  useSearch: false,
  spellcheck: false,
  showCharsCounter: false,
  showWordsCounter: false,
  showXPathInStatusbar: false,
  // Hide Jodit's hover "break" line + enter icon between blocks.
  addNewLine: false,
  addNewLineOnDBLClick: false,
  askBeforePasteHTML: false,
  askBeforePasteFromWord: false,
  defaultActionOnPaste: "insert_clear_html",
  theme:
    typeof document !== "undefined" &&
    document.documentElement.getAttribute("data-theme") === "light"
      ? "default"
      : "dark",
  // Body-mounted popups get shared PostEditor chrome (joditPopup.theme.scss).
  popupRoot:
    typeof document !== "undefined" ? document.body : undefined,
  buttons: "bold,italic,ul,link",
  placeholder: "What are you seeing in the market?",
  readOnly: false,
  link: {
    noFollowCheckbox: false,
    openInNewTabCheckbox: false,
    modeClassName: "",
  },
  controls: {
    ul: {
      command: "insertUnorderedList",
      tags: ["ul"],
      tooltip: "Bullet list",
    },
  },
};

const getParams = (pathname) => {
  if (!pathname || typeof pathname !== "string") {
    return {};
  }
  const matchBlog = matchPath(`/alerts/:postName`, pathname);
  return (matchBlog && matchBlog.params) || {};
};

const topicsType = [
  { key: "*", name: "All" },
  { key: "info", name: "Informational" },
  { key: "trade&opened", name: "Open Trade" },
  { key: "trade&closed", name: "Closed Trade" },
  { key: "tracking", name: "Tracking" },
];

const stripAttachment = (att) => {
  if (!att) return att;
  const { localId, source, ...rest } = att;
  return rest;
};

/** Full image at zoom 1. Zoom above 1 trims into a 3:2 frame. */
const renderAlertImage = (src, { cropZoom = 1, cropX = 50, cropY = 50 } = {}) =>
  new Promise((resolve, reject) => {
    const source = new Image();
    source.onload = () => {
      const maxEdge = 1600;
      const naturalW = source.naturalWidth;
      const naturalH = source.naturalHeight;
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      if (!context || !naturalW || !naturalH) {
        reject(new Error("image"));
        return;
      }
      if (cropZoom <= 1.01) {
        const scale = Math.min(1, maxEdge / Math.max(naturalW, naturalH));
        canvas.width = Math.max(1, Math.round(naturalW * scale));
        canvas.height = Math.max(1, Math.round(naturalH * scale));
        context.drawImage(source, 0, 0, canvas.width, canvas.height);
      } else {
        const outputWidth = 1200;
        const outputHeight = 800;
        const targetRatio = outputWidth / outputHeight;
        let cropWidth = naturalW;
        let cropHeight = cropWidth / targetRatio;
        if (cropHeight > naturalH) {
          cropHeight = naturalH;
          cropWidth = cropHeight * targetRatio;
        }
        cropWidth /= cropZoom;
        cropHeight /= cropZoom;
        const centerX = (cropX / 100) * naturalW;
        const centerY = (cropY / 100) * naturalH;
        const sourceX = Math.min(
          naturalW - cropWidth,
          Math.max(0, centerX - cropWidth / 2)
        );
        const sourceY = Math.min(
          naturalH - cropHeight,
          Math.max(0, centerY - cropHeight / 2)
        );
        canvas.width = outputWidth;
        canvas.height = outputHeight;
        context.drawImage(
          source,
          sourceX,
          sourceY,
          cropWidth,
          cropHeight,
          0,
          0,
          outputWidth,
          outputHeight
        );
      }
      resolve(canvas.toDataURL("image/jpeg", 0.92));
    };
    source.onerror = () => reject(new Error("image"));
    source.src = src;
  });

const emptyVideoUpload = {
  active: false,
  progress: 0,
  fileName: "",
  error: "",
};

const getAlertTopicLabel = (alert) => {
  if (alert?.isInformational) return "Informational";
  if (alert?.alertStatusId === 1) return "Open Trade";
  if (alert?.alertStatusId === 2) return "Closed Trade";
  if (alert?.tracking) return "Tracking";
  return "Alert";
};

/** Lovable-style absolute date for thread rows (e.g. "Aug 25, 2026"). */
const formatAlertDay = (dateValue) => {
  if (!dateValue) return null;
  const normalized = String(dateValue).replace(" ", "T");
  const parsed = dayjs.utc(
    normalized.endsWith("Z") ? normalized : `${normalized}Z`
  );
  if (!parsed.isValid()) return formatToLocalDate(dateValue);
  return parsed.local().format("MMM D, YYYY");
};

class Alerts extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      alertType: false,
      units: [],
      selectedUnit: null,
      selectedDurability: 1,
      topicsSelected: ["*"],
      alerts: [],
      desc: "",
      image: "",
      currentPage: 0,
      contentType: false,
      wistiaConfig: null,
      updateMode: false,
      attachments: [],
      cropEdit: null,
      imageAdding: false,
      cropZoom: 1,
      cropX: 50,
      cropY: 50,
      publisherSelected: null,
      showVideoUpload: false,
      videoPreviewOpen: false,
      videoUpload: { ...emptyVideoUpload },
      loading: false,
      publishers: publisherStore.getPublishers() || [],
      expandedImage: null,
    };
    this.alertEditorRef = React.createRef();
    this.fileInputRef = React.createRef();
    this.publishRef = React.createRef();
    this.onPublisherStoreChange = this.onPublisherStoreChange.bind(this);
  }

  onPublisherStoreChange() {
    this.setState({
      publishers: publisherStore.getPublishers() || [],
    });
  }

  componentDidMount = async () => {
    publisherStore.addChangeListener(this.onPublisherStoreChange);
    if (publisherStore.getPublishers().length === 0) {
      getPublishers();
    }

    this.setState({ loading: true });
    const alertId = this.props.match?.params?.id;
    if (alertId) {
      await this.getSingleAlert(alertId);
    } else {
      this.reloadData();
      // Guests only get the first page — infinite scroll is members-only.
      if (localStorage.getItem("user") !== null) {
        document.addEventListener("scroll", this.trackScrolling);
      }
    }
  };

  getSingleAlert = async (alertId) => {
    let user;
    if (localStorage.getItem("user") !== null) {
      user = JSON.parse(localStorage.getItem("user"));
    }
    if (!user) {
      // user not logged in.
    } else {
      let alert = await getAlert(alertId);
      let alerts = [];
      alerts.push(alert.data);
      this.setState(
        {
          alerts: alerts,
          user: user,
          loading: false,
        },
        () => {
          window.scrollTo(0, 0);
        }
      );
    }
  };

  closeExpandedImageOnEscape = (event) => {
    if (event.key === "Escape") {
      this.setState({ expandedImage: null });
    }
  };

  openContentImage = (event) => {
    const img = event.target.closest && event.target.closest("img");
    if (!img) return;
    const content = img.closest(".alerts-feed-card__content");
    if (!content || img.closest(".alerts-feed-card__media")) return;
    const src = img.currentSrc || img.src;
    if (!src) return;
    event.preventDefault();
    this.setState({ expandedImage: src });
  };

  componentDidUpdate(prevProps, prevState) {
    if (this.state.expandedImage && !prevState.expandedImage) {
      document.addEventListener("keydown", this.closeExpandedImageOnEscape);
      document.body.style.overflow = "hidden";
    } else if (!this.state.expandedImage && prevState.expandedImage) {
      document.removeEventListener("keydown", this.closeExpandedImageOnEscape);
      document.body.style.overflow = "";
    }

    const pathname = this.props.location?.pathname;
    const prevPathname = prevProps.location?.pathname;

    if (!pathname || !prevPathname) {
      return; // Exit early if pathname is not available
    }

    const currentParams = getParams(pathname);
    const prevParams = getParams(prevPathname);

    if (
      currentParams.postName &&
      currentParams.postName !== prevParams.postName
    ) {
      this.getSingleAlert(currentParams.postName);
    }
  }

  componentWillUnmount = () => {
    document.removeEventListener("scroll", this.trackScrolling);
    document.removeEventListener("keydown", this.closeExpandedImageOnEscape);
    document.body.style.overflow = "";
    publisherStore.removeChangeListener(this.onPublisherStoreChange);
  };

  trackScrolling = async () => {
    // Guests see gated cards only — don't paginate an endless lock wall.
    if (!this.state.user) {
      document.removeEventListener("scroll", this.trackScrolling);
      return;
    }

    const wrappedElement = document.getElementById("alerts-feed-scroll");
    if (this.isBottom(wrappedElement)) {
      document.removeEventListener("scroll", this.trackScrolling);
      let nextPage = this.state.currentPage + 1;
      let publisherId = "*";
      if (this.props.location?.state) {
        publisherId = this.props.location.state.analyst;
      }
      if (this.state.publisherSelected) {
        publisherId = this.state.publisherSelected;
      }
      const alerts = await getMixedAlerts(
        publisherId,
        this.state.topicsSelected,
        10,
        nextPage
      );
      let temp = this.state.alerts;
      let updated = temp.concat(alerts.data.items);
      this.setState(
        {
          alerts: updated,
          currentPage: nextPage,
        },
        () => {
          if (this.state.user) {
            document.addEventListener("scroll", this.trackScrolling);
          }
        }
      );
    }
  };

  isBottom(el) {
    return el && el.getBoundingClientRect().bottom <= window.innerHeight;
  }

  revealUpdatedAlert = (alertId) => {
    window.requestAnimationFrame(() => {
      const thread = document.getElementById(`alert-thread-${alertId}`);
      const card = document.getElementById(`alert-card-${alertId}`);
      const target = thread?.lastElementChild || card;
      if (!target) return;
      const top =
        target.getBoundingClientRect().top + window.scrollY - 96;
      window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    });
  };

  reloadData = async ({ focusAlertId } = {}) => {
    let user;
    if (localStorage.getItem("user") !== null) {
      user = JSON.parse(localStorage.getItem("user"));
    }
    let alerts;
    let units;
    let wistiaConfig;
    let publisherId = "*";
    if (this.props.location?.state) {
      publisherId = this.props.location.state.analyst;
    }
    if (this.state.publisherSelected) {
      publisherId = this.state.publisherSelected;
    }
    const pageSize = focusAlertId ? (this.state.currentPage + 1) * 10 : 10;
    const pageIndex = focusAlertId ? 0 : this.state.currentPage;
    if (user && (user.userRoleId === 1 || user.userRoleId === 3)) {
      const res = await getWistiaConfig();
      wistiaConfig = res.data;
      alerts = await getMixedAlerts(
        publisherId,
        this.state.topicsSelected,
        pageSize,
        pageIndex
      );
      units = await getTradingUnit();
      this.initVideoUpload(wistiaConfig);
    }
    if (user && user.userRoleId === 2) {
      alerts = await getMixedAlerts(
        publisherId,
        this.state.topicsSelected,
        pageSize,
        pageIndex
      );
    }
    if (!user) {
      alerts = await getMixedAlerts(
        publisherId,
        this.state.topicsSelected,
        pageSize,
        pageIndex
      );
    }
    const items = (alerts.data.items || []).map((alert) =>
      focusAlertId && alert.id === focusAlertId
        ? { ...alert, updatesOpen: true }
        : alert
    );
    this.setState(
      {
        alerts: items,
        wistiaConfig: wistiaConfig,
        units: units ? units.data : null,
        user: user,
        attachments: [],
        cropEdit: null,
        imageAdding: false,
        cropZoom: 1,
        cropX: 50,
        cropY: 50,
        desc: "",
        image: "",
        entry: "",
        target: "",
        stop: "",
        loading: false,
        showVideoUpload: false,
        videoPreviewOpen: false,
        videoUpload: { ...emptyVideoUpload },
      },
      () => {
        if (focusAlertId) {
          this.revealUpdatedAlert(focusAlertId);
        } else {
          window.scrollTo(0, 0);
        }
      }
    );
  };

  type = () => {
    this.setState({ alertType: !this.state.alertType });
  };

  getTradingUnitId = () => {
    if (!this.state.alertType) return null;
    const unit = this.state.selectedUnit;
    if (unit && typeof unit === "object") return unit.id || null;
    if (!unit || unit === "Choose...") return null;
    return unit;
  };

  publish = async () => {
    if (this.state.videoUpload.active) {
      showLovableToast("Wait for the video to finish uploading.");
      return;
    }
    if (this._addingImages || this.state.imageAdding) {
      showLovableToast("Wait for the images to finish adding.");
      return;
    }
    const attachments = await this.applyOpenCrop();
    const data = {
      alertDurabilityId: this.state.selectedDurability,
      tradingUnitId: this.getTradingUnitId(),
      isInformational: !this.state.alertType,
      entry: this.state.entry,
      target: this.state.target,
      stopLoss: this.state.stop,
      content: this.state.desc,
      attachments: (attachments || []).map(stripAttachment),
    };
    if (this.state.updateMode) {
      data["alertId"] = this.state.updateAlert.id;
      let update;
      try {
        update = await createAlertUpdate(data);
      } catch (error) {
        update = error;
      }
      if (update && update.status >= 200 && update.status < 300) {
        const alertId = this.state.updateAlert.id;
        this.clear();
        showLovableToast("Alert updated");
        this.reloadData({ focusAlertId: alertId });
      } else {
        const errorResponse = handleApiError(
          update,
          "Failed to update alert",
          { notify: false }
        );
        this.setState({
          showError: true,
          errorText: errorResponse.message,
        });
        showLovableToast(errorResponse.message);
      }
    } else {
      const res = await createAlert(data);
      if (res && res.status >= 200 && res.status < 300) {
        this.setState(
          {
            currentPage: 0,
            alertCreated: true,
            showError: false,
          },
          () => {
            this.reloadData();
          }
        );
        showLovableToast("Alert published");
      } else {
        const errorResponse = handleApiError(res, "Failed to create alert", {
          notify: false,
        });
        this.setState({
          showError: true,
          errorText: errorResponse.message,
        });
        showLovableToast(errorResponse.message);
      }
    }
  };

  changeType = (event) => {
    this.setState({ selectedDurability: event.target.value });
  };

  updateAlert = (alert) => {
    this.setState(
      {
        updateMode: true,
        alertCreated: false,
        alertUpdated: false,
        alertType: !alert.isInformational,
        entry: alert.entry,
        target: alert.target,
        stop: alert.stopLoss,
        desc: alert.content || "",
        updateAlert: alert,
        selectedUnit: alert.tradingUnit,
        selectedDurability: alert.alertDurabilityId || 1,
      },
      () => {
        const composer = this.publishRef.current;
        if (composer && typeof composer.scrollIntoView === "function") {
          composer.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
          window.scrollTo(0, 0);
        }
      }
    );
  };

  initVideoUpload = (config) => {
    if (!config) return;
    var self = this;
    window._wapiq = window._wapiq || [];
    window._wapiq.push((W) => {
      window.wistiaUploader = new W.Uploader({
        accessToken: config.accessToken,
        dropIn: "wistia_uploader-alt",
        projectId: config.projectId,
        projectName: config.projectName,
      });
      window.wistiaUploader.bind("uploadstart", (file) => {
        self.setState({
          showVideoUpload: false,
          videoPreviewOpen: false,
          videoUpload: {
            active: true,
            progress: 0,
            fileName: file?.name || "Video",
            error: "",
          },
        });
      });
      window.wistiaUploader.bind("uploadprogress", (file, progress) => {
        const pct = Math.min(100, Math.round(Number(progress) * 100));
        self.setState((prev) => ({
          videoUpload: {
            ...prev.videoUpload,
            active: true,
            progress: Number.isFinite(pct) ? pct : prev.videoUpload.progress,
            fileName: file?.name || prev.videoUpload.fileName,
            error: "",
          },
        }));
      });
      window.wistiaUploader.bind("uploadfailed", () => {
        self.setState({
          showVideoUpload: false,
          videoUpload: {
            ...emptyVideoUpload,
            error: "Video upload failed",
          },
        });
        showLovableToast("Video upload failed");
      });
      window.wistiaUploader.bind("uploadcancelled", () => {
        self.setState({
          showVideoUpload: false,
          videoUpload: { ...emptyVideoUpload },
        });
      });
      window.wistiaUploader.bind(
        "uploadembeddable",
        (file, media, embedCode) => {
          const videoAtt = {
            attachmentTypeId: 2,
            mediaId: media.id,
            thumbnailUrl: media.thumbnail?.url || "",
            embedCode: embedCode,
          };
          self.setState(
            (prev) => ({
            embeddedVideo: {
              mediaId: media.id,
              thumbnailUrl: videoAtt.thumbnailUrl,
              embedCode: embedCode,
            },
            showVideoUpload: false,
            videoPreviewOpen: true,
            videoUpload: { ...emptyVideoUpload },
            attachments: [
              ...prev.attachments.filter((a) => a.attachmentTypeId !== 2),
              videoAtt,
            ],
            }),
            () => {
              self.setupComposerWistia(media.id);
            }
          );
        }
      );
    });
  };

  setDescription = (event) => {
    this.setState({ desc: event });
  };

  clear = () => {
    this.setState({
      updateMode: false,
      alertType: false,
      entry: 0,
      target: 0,
      stop: 0,
      desc: "",
      attachments: [],
      cropEdit: null,
      imageAdding: false,
      cropZoom: 1,
      cropX: 50,
      cropY: 50,
      updateAlert: null,
      selectedUnit: null,
      selectedDurability: 1,
      showError: false,
      alertCreated: false,
      alertUpdated: false,
      showVideoUpload: false,
      videoPreviewOpen: false,
      videoUpload: { ...emptyVideoUpload },
    });
  };

  resetComposerFields = () => {
    this.setState({
      desc: "",
      attachments: [],
      cropEdit: null,
      imageAdding: false,
      cropZoom: 1,
      cropX: 50,
      cropY: 50,
      entry: 0,
      target: 0,
      stop: 0,
      selectedUnit: null,
      selectedDurability: 1,
      showError: false,
      alertCreated: false,
      alertUpdated: false,
      showVideoUpload: false,
      videoPreviewOpen: false,
      videoUpload: { ...emptyVideoUpload },
    });
  };

  clickWistiaFileInput = () => {
    const root = document.getElementById("wistia_uploader-alt");
    const input = root?.querySelector('input[type="file"]');
    if (!input) return false;
    input.click();
    return true;
  };

  openVideoPicker = () => {
    if (this.state.videoUpload.active) return;
    if (this.clickWistiaFileInput()) {
      this.setState({
        showVideoUpload: false,
        videoUpload: { ...this.state.videoUpload, error: "" },
      });
      return;
    }
    this.setState({ showVideoUpload: true }, () => {
      window.setTimeout(() => {
        if (this.clickWistiaFileInput()) {
          this.setState({ showVideoUpload: false });
        }
      }, 80);
    });
  };

  createFavoriteAlert = async (alert) => {
    this.state.alerts.forEach((a) => {
      if (a.id === alert.id) {
        a.isFavourite = alert.isFavourite ? false : true;
      }
    });
    this.setState({
      alerts: this.state.alerts,
    });
    if (alert.isFavourite) {
      await createFavoriteAlert(alert.id);
    } else {
      await removeFavoriteAlert(alert.id);
    }
  };

  revertTradeTracking = (alertId, tracking) => {
    this.state.alerts.forEach((a) => {
      if (a.id === alertId) {
        a.tracking = tracking;
      }
    });
    this.setState({ alerts: this.state.alerts });
  };

  trackTrade = async (alert) => {
    if (this.trackingAlertId) return;
    const wasTracking = !!alert.tracking;
    const nextTracking = !wasTracking;
    this.trackingAlertId = alert.id;
    this.state.alerts.forEach((a) => {
      if (a.id === alert.id) {
        a.tracking = nextTracking;
      }
    });
    this.setState({
      alerts: this.state.alerts,
    });
    const failureMessage = nextTracking
      ? "Couldn't start tracking this trade"
      : "Couldn't stop tracking this trade";
    try {
      const res = await setrackingAlert(alert.id, nextTracking);
      if (res && res.status >= 200 && res.status < 300) {
        NotificationService.success(
          nextTracking
            ? "Now tracking this trade"
            : "Stopped tracking this trade"
        );
        return;
      }
      this.revertTradeTracking(alert.id, wasTracking);
      handleApiError(res, failureMessage);
    } catch (error) {
      this.revertTradeTracking(alert.id, wasTracking);
      handleApiError(error, failureMessage);
    } finally {
      this.trackingAlertId = null;
    }
  };

  showShare = (alert) => {
    const nextOpen = !alert.showShare;
    this.state.alerts.forEach((a) => {
      a.showShare = a.id === alert.id ? nextOpen : false;
    });
    this.setState({
      alerts: this.state.alerts,
    });
  };

  renderAlertTimestamp = (dateValue, className = "alerts-feed-card__date") => {
    const absolute = formatToLocalDate(dateValue);
    const relative = formatRelativeTime(dateValue);

    if (!absolute) {
      return null;
    }

    return (
      <time
        className={className}
        dateTime={dateValue}
        title={absolute}
      >
        {relative}
      </time>
    );
  };

  toggleUpdates = (alert) => {
    this.state.alerts.forEach((a) => {
      if (a.id === alert.id) {
        a.updatesOpen = a.updatesOpen ? !a.updatesOpen : true;
      }
    });
    this.setState({
      alerts: this.state.alerts,
    });
  };

  addImages = (files) => {
    if (this._addingImages || !files || !files.length) return;
    const list = Array.from(files).filter((file) =>
      file.type.startsWith("image/")
    );
    if (!list.length) return;
    this._addingImages = true;
    this.setState({ imageAdding: true });
    Promise.all(
      list.map(
        (file, index) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => {
              const source = reader.result;
              renderAlertImage(source)
                .then((data) =>
                  resolve({
                    localId: `${file.name}-${file.lastModified}-${index}-${Date.now()}`,
                    attachmentTypeId: 1,
                    title: file.name || "Image",
                    data,
                    source,
                  })
                )
                .catch(() => resolve(null));
            };
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(file);
          })
      )
    ).then((pending) => {
      this._addingImages = false;
      const added = pending.filter(Boolean);
      if (added.length < list.length) {
        showLovableToast(
          added.length
            ? "One image could not be added."
            : "These images could not be added."
        );
      }
      this.setState((prev) => ({
        imageAdding: false,
        attachments: [...prev.attachments, ...added],
      }));
    });
  };

  openImagePicker = () => {
    this.fileInputRef.current?.click();
  };

  resetCropState = () => ({
    cropZoom: 1,
    cropX: 50,
    cropY: 50,
  });

  openCrop = (att) => {
    if (!att || att.attachmentTypeId !== 1) return;
    this.setState({
      cropEdit: { localId: att.localId },
      ...this.resetCropState(),
    });
  };

  closeCrop = () => {
    this.setState({
      cropEdit: null,
      ...this.resetCropState(),
    });
  };

  applyOpenCrop = () =>
    new Promise((resolve) => {
      const edit = this.state.cropEdit;
      const attachments = this.state.attachments || [];
      if (!edit) {
        resolve(attachments);
        return;
      }
      const current = attachments.find((item) => item.localId === edit.localId);
      if (!current) {
        this.setState({ cropEdit: null, ...this.resetCropState() }, () =>
          resolve(this.state.attachments || [])
        );
        return;
      }
      const { cropZoom, cropX, cropY } = this.state;
      renderAlertImage(current.source || current.data, {
        cropZoom,
        cropX,
        cropY,
      })
        .then((data) => {
          let next = attachments;
          this.setState(
            (prev) => {
              next = (prev.attachments || []).map((item) =>
                item.localId === edit.localId ? { ...item, data } : item
              );
              return {
                attachments: next,
                cropEdit: null,
                ...this.resetCropState(),
              };
            },
            () => resolve(next)
          );
        })
        .catch(() => {
          showLovableToast("This image could not be cropped.");
          resolve(attachments);
        });
    });

  removeAttachment = (att) => {
    if (att.attachmentTypeId === 1) {
      this.setState((prev) => ({
        attachments: prev.attachments.filter((item) => item.localId !== att.localId),
        cropEdit:
          prev.cropEdit && prev.cropEdit.localId === att.localId
            ? null
            : prev.cropEdit,
        ...(prev.cropEdit && prev.cropEdit.localId === att.localId
          ? this.resetCropState()
          : {}),
      }));
    }
    if (att.attachmentTypeId === 2) {
      this.setState({
        embeddedVideo: null,
        showVideoUpload: false,
        videoPreviewOpen: false,
        videoUpload: { ...emptyVideoUpload },
        attachments: this.state.attachments.filter(
          (a) => a.attachmentTypeId !== 2
        ),
      });
    }
  };

  mapAttachments = (att) => {
    if (att.attachmentTypeId === 2) {
      return {
        embedUrl: att.mediaId,
        thumbnail:
          "https://encrypted-tbn0.gstatic.com/images?q=tbn%3AANd9GcTCD7-zFxjl5EXkC5XKGn6xw0yxVb9L6Kng0A&usqp=CAU",
        renderItem: this._renderVideo.bind(this),
      };
    } else {
      return {
        original: att.url,
        thumbnail: att.url,
      };
    }
  };

  _renderVideo(item) {
    return <div className={"wistia_embed wistia_async_" + item.embedUrl}></div>;
  }

  setupComposerWistia = (mediaId) => {
    if (!mediaId) return;
    window._wq = window._wq || [];
    window._wq.push({
      id: mediaId,
      options: { autoPlay: true, playsinline: true },
    });
    if (window.Wistia?.embeds?.setup) {
      window.Wistia.embeds.setup();
    }
  };

  closeTrade = async () => {
    if (this.state.videoUpload.active) {
      showLovableToast("Wait for the video to finish uploading.");
      return;
    }
    if (this._addingImages || this.state.imageAdding) {
      showLovableToast("Wait for the images to finish adding.");
      return;
    }
    const attachments = await this.applyOpenCrop();
    const data = {
      closeAlert: true,
      alertId: this.state.updateAlert.id,
      content: this.state.desc === "" ? "Trade closed." : this.state.desc,
      attachments: (attachments || []).map(stripAttachment),
      entry: this.state.entry,
      target: this.state.target,
      stopLoss: this.state.stop,
    };
    if (this.state.updateMode) {
      let update;
      try {
        update = await createAlertUpdate(data);
      } catch (error) {
        update = error;
      }
      if (update && update.status >= 200 && update.status < 300) {
        const alertId = this.state.updateAlert.id;
        this.clear();
        showLovableToast("Trade closed");
        this.reloadData({ focusAlertId: alertId });
      } else {
        const errorResponse = handleApiError(update, "Failed to close alert", {
          notify: false,
        });
        this.setState({
          showError: true,
          errorText: errorResponse.message,
        });
        showLovableToast(errorResponse.message);
      }
    }
  };

  changeAlertFilter = (value) => {
    this.setState(
      { topicsSelected: value, currentPage: 0 },
      () => {
        this.reloadData();
      }
    );
  };

  clearFilters = () => {
    this.setState(
      {
        publisherSelected: null,
        topicsSelected: "*",
        currentPage: 0,
      },
      () => {
        this.reloadData();
      }
    );
  };

  selectPublisher = (event, id) => {
    if (event && typeof event.preventDefault === "function") {
      event.preventDefault();
    }

    const nextPublisherId =
      this.state.publisherSelected === id ? null : id;

    this.setState(
      {
        currentPage: 0,
      },
      () => {
        this.setState({ publisherSelected: nextPublisherId }, () => {
          this.reloadData();
        });
      }
    );
  };

  render() {
    const topicValue = Array.isArray(this.state.topicsSelected)
      ? this.state.topicsSelected[0] || "*"
      : this.state.topicsSelected || "*";
    const hasActiveFilters =
      !!this.state.publisherSelected || topicValue !== "*";
    const hasAlerts = (this.state.alerts || []).length > 0;

    return this.state.loading ? (
      <AlertsFeedSkeleton />
    ) : (
      <div
        className="alerts-lovable__core"
        id="alerts-feed-scroll"
        onClick={this.openContentImage}
      >
          {this.state.user != null && this.state.user.userRoleId !== 2 && (() => {
            const mePublisher =
              (this.state.publishers || []).find(
                (p) => p.id === this.state.user.id
              ) || null;
            const authorName =
              mePublisher?.name || this.state.user.name || "Publisher";
            const authorAvatar = mePublisher
              ? getPublisherAvatarUrl(mePublisher)
              : this.state.user?.avatar?.small || null;
            const units = Array.isArray(this.state.units)
              ? this.state.units
              : [];
            const tickerValue =
              this.state.selectedUnit &&
              typeof this.state.selectedUnit === "object"
                ? this.state.selectedUnit.id
                : this.state.selectedUnit || "";
            const isLong = Number(this.state.selectedDurability) === 2;
            const updateTicker =
              this.state.updateAlert?.tradingUnit?.abbreviation ||
              this.state.selectedUnit?.abbreviation ||
              null;
            const imageAtts = (this.state.attachments || []).filter(
              (att) => att.attachmentTypeId !== 2
            );
            const cropEdit = this.state.cropEdit;
            const cropAtt = cropEdit
              ? imageAtts.find((att) => att.localId === cropEdit.localId)
              : null;
            const videoAtt = (this.state.attachments || []).find(
              (att) => att.attachmentTypeId === 2
            );
            const hasVideo = !!videoAtt;
            const videoUploading = !!this.state.videoUpload?.active;
            const videoUploadPct = this.state.videoUpload?.progress || 0;
            const videoOpen = this.state.showVideoUpload && !videoUploading;
            const editorCfg = {
              ...editorConfig,
              ...(this.state.updateMode
                ? { placeholder: "What changed since the original call?" }
                : {}),
            };

            return (
            <div
              className="alerts-lovable__publish-wrap"
              ref={this.publishRef}
            >
            <Card className="alerts-publish mb-3">
              <CardBody className="alerts-publish__body">
                <div className="alerts-publish__masthead">
                  <div className="alerts-publish__author">
                    {authorAvatar ? (
                      <img
                        src={authorAvatar}
                        alt=""
                        className="alerts-publish__avatar"
                      />
                    ) : (
                      <span className="alerts-publish__avatar-fallback">
                        {String(authorName).slice(0, 1)}
                      </span>
                    )}
                    <div className="alerts-publish__author-text">
                      <p>Publishing as {authorName}</p>
                      <span>
                        {this.state.updateMode
                          ? `Updating alert created on ${formatToLocalDate(
                              this.state.updateAlert?.createdAt
                            )}`
                          : "Goes live to every member instantly"}
                      </span>
                    </div>
                    <span className="alerts-publish__studio">Studio</span>
                  </div>
                </div>

                <div className="alerts-publish__form">
                  {!this.state.updateMode && (
                    <div className="alerts-publish__kinds">
                      <button
                        type="button"
                        className={`alerts-publish__kind${
                          !this.state.alertType ? " is-active" : ""
                        }`}
                        aria-pressed={!this.state.alertType}
                        onClick={() =>
                          this.state.alertType && this.type()
                        }
                      >
                        Informational
                      </button>
                      <button
                        type="button"
                        className={`alerts-publish__kind${
                          this.state.alertType ? " is-active" : ""
                        }`}
                        aria-pressed={!!this.state.alertType}
                        onClick={() =>
                          !this.state.alertType && this.type()
                        }
                      >
                        Trade Alert
                      </button>
                    </div>
                  )}

                  {this.state.updateMode && this.state.alertType && (
                    <div className="alerts-publish__locked">
                      {updateTicker ? (
                        <span className="alerts-publish__chip">
                          ${updateTicker}
                        </span>
                      ) : null}
                      <span
                        className={`alerts-publish__chip alerts-publish__chip--side${
                          isLong ? " is-long" : " is-short"
                        }`}
                      >
                        {isLong ? "Long" : "Short"}
                      </span>
                      <span className="alerts-publish__locked-hint">
                        Ticker and direction are locked on updates
                      </span>
                    </div>
                  )}

                  <div className="alerts-publish__editor-panel cms-editor-form">
                    <JoditEditor
                      key={
                        this.state.updateMode
                          ? `update-${this.state.updateAlert?.id}`
                          : "new-alert"
                      }
                      ref={this.alertEditorRef}
                      value={this.state.desc}
                      config={editorCfg}
                      tabIndex={1}
                      onBlur={(newContent) => this.setDescription(newContent)}
                    />
                  </div>

                  <div className="alerts-publish__media">
                    <div className="alerts-publish__media-row">
                      <input
                        ref={this.fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="alerts-publish__file-input"
                        onChange={(e) => {
                          this.addImages(e.target.files);
                          e.target.value = "";
                        }}
                      />
                      <button
                        type="button"
                        className="alerts-publish__add-image"
                        onClick={this.openImagePicker}
                        disabled={this.state.imageAdding}
                      >
                        <i className="material-icons" aria-hidden="true">
                          add_photo_alternate
                        </i>
                        {this.state.imageAdding ? "Adding…" : "Add images"}
                      </button>
                      <button
                        type="button"
                        className={`alerts-publish__add-image${
                          videoOpen ? " is-active" : ""
                        }`}
                        aria-pressed={videoOpen}
                        disabled={videoUploading}
                        onClick={this.openVideoPicker}
                      >
                        <i className="material-icons" aria-hidden="true">
                          movie
                        </i>
                        {videoUploading
                          ? "Uploading…"
                          : hasVideo
                          ? "Replace video"
                          : "Add video"}
                      </button>
                    </div>
                    <div
                      id="wistia_uploader-alt"
                      className={videoOpen ? "is-open" : "is-collapsed"}
                    />
                    {cropAtt && (
                      <div className="alerts-publish__crop-panel">
                        <div className="alerts-publish__crop-head">
                          <span className="alerts-publish__crop-icon" aria-hidden="true">
                            <i className="material-icons">crop</i>
                          </span>
                          <div className="alerts-publish__crop-copy">
                            <p>Crop image</p>
                            <span>Optional. Zoom 1× keeps the full image.</span>
                          </div>
                          <button
                            type="button"
                            className="alerts-publish__crop-discard"
                            title="Close crop"
                            aria-label="Close crop"
                            onClick={this.closeCrop}
                          >
                            <i className="material-icons">close</i>
                          </button>
                        </div>
                        <div className="alerts-publish__crop-body">
                          <div className="alerts-publish__crop-stage">
                            <img
                              src={cropAtt.source || cropAtt.data}
                              alt="Image being cropped"
                              draggable={false}
                              style={{
                                objectPosition: `${this.state.cropX}% ${this.state.cropY}%`,
                                transform: `scale(${this.state.cropZoom})`,
                                transformOrigin: `${this.state.cropX}% ${this.state.cropY}%`,
                              }}
                            />
                          </div>
                          <div className="alerts-publish__crop-controls">
                            <label>
                              <span>
                                Zoom
                                <span>
                                  {Number(this.state.cropZoom).toFixed(1)}×
                                </span>
                              </span>
                              <input
                                type="range"
                                min="1"
                                max="3"
                                step="0.1"
                                value={this.state.cropZoom}
                                onChange={(event) =>
                                  this.setState({
                                    cropZoom: Number(event.target.value),
                                  })
                                }
                              />
                            </label>
                            <label>
                              <span>Horizontal</span>
                              <input
                                type="range"
                                min="0"
                                max="100"
                                value={this.state.cropX}
                                onChange={(event) =>
                                  this.setState({
                                    cropX: Number(event.target.value),
                                  })
                                }
                              />
                            </label>
                            <label>
                              <span>Vertical</span>
                              <input
                                type="range"
                                min="0"
                                max="100"
                                value={this.state.cropY}
                                onChange={(event) =>
                                  this.setState({
                                    cropY: Number(event.target.value),
                                  })
                                }
                              />
                            </label>
                            <button
                              type="button"
                              className="alerts-publish__crop-apply"
                              onClick={() => this.applyOpenCrop()}
                            >
                              <i className="material-icons" aria-hidden="true">
                                check
                              </i>
                              Apply crop
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    {imageAtts.length > 0 || hasVideo || videoUploading ? (
                      <div className="alerts-publish__image-grid">
                        {imageAtts.map((att) => (
                          <div
                            key={att.localId || att.data}
                            className={`alerts-publish__image-thumb${
                              cropAtt && cropAtt.localId === att.localId
                                ? " is-cropping"
                                : ""
                            }`}
                          >
                            <img
                              src={att.data}
                              alt={att.title || "Attachment"}
                            />
                            <button
                              type="button"
                              className="alerts-publish__image-crop"
                              onClick={() => this.openCrop(att)}
                              aria-label="Crop image"
                            >
                              <i className="material-icons">crop</i>
                            </button>
                            <button
                              type="button"
                              className="alerts-publish__image-remove"
                              onClick={() => this.removeAttachment(att)}
                              aria-label="Remove image"
                            >
                              <i className="material-icons">close</i>
                            </button>
                          </div>
                        ))}
                        {videoUploading && (
                          <div
                            className="alerts-publish__image-thumb alerts-publish__image-thumb--video is-uploading"
                            role="status"
                            aria-live="polite"
                            title={this.state.videoUpload.fileName || "Uploading video"}
                          >
                            <span className="alerts-publish__video-chip-icon">
                              <i className="material-icons" aria-hidden="true">
                                movie
                              </i>
                            </span>
                            <span className="alerts-publish__video-progress-label">
                              {videoUploadPct < 100
                                ? `${videoUploadPct}%`
                                : "Processing"}
                            </span>
                            <span className="alerts-publish__video-progress-bar">
                              <span
                                style={{
                                  width: `${
                                    videoUploadPct < 100 ? videoUploadPct : 100
                                  }%`,
                                }}
                              />
                            </span>
                          </div>
                        )}
                        {hasVideo && !videoUploading && (
                          <div
                            className={`alerts-publish__image-thumb alerts-publish__image-thumb--video${
                              this.state.videoPreviewOpen ? " is-playing" : ""
                            }`}
                          >
                            <button
                              type="button"
                              className="alerts-publish__video-thumb-play"
                              aria-label={
                                this.state.videoPreviewOpen
                                  ? "Hide video preview"
                                  : "Play video preview"
                              }
                              onClick={() =>
                                this.setState(
                                  (prev) => ({
                                    videoPreviewOpen: !prev.videoPreviewOpen,
                                  }),
                                  () => {
                                    if (this.state.videoPreviewOpen) {
                                      this.setupComposerWistia(videoAtt.mediaId);
                                    }
                                  }
                                )
                              }
                            >
                              {videoAtt.thumbnailUrl ? (
                                <img
                                  src={videoAtt.thumbnailUrl}
                                  alt=""
                                />
                              ) : (
                                <span className="alerts-publish__video-chip-icon">
                                  <i className="material-icons" aria-hidden="true">
                                    movie
                                  </i>
                                </span>
                              )}
                              <span className="alerts-publish__video-thumb-overlay">
                                <span className="alerts-publish__video-thumb-icon">
                                  <i className="material-icons" aria-hidden="true">
                                    {this.state.videoPreviewOpen
                                      ? "pause"
                                      : "play_arrow"}
                                  </i>
                                </span>
                              </span>
                            </button>
                            <button
                              type="button"
                              className="alerts-publish__image-remove"
                              onClick={() => this.removeAttachment(videoAtt)}
                              aria-label="Remove video"
                            >
                              <i className="material-icons">close</i>
                            </button>
                          </div>
                        )}
                      </div>
                    ) : null}
                    {hasVideo &&
                      !videoUploading &&
                      this.state.videoPreviewOpen && (
                        <div className="alerts-publish__video-preview">
                          <div className="alerts-publish__video-preview-head">
                            <i className="material-icons" aria-hidden="true">
                              movie
                            </i>
                            <span>Video preview</span>
                            <button
                              type="button"
                              aria-label="Hide video preview"
                              onClick={() =>
                                this.setState({ videoPreviewOpen: false })
                              }
                            >
                              <i className="material-icons">close</i>
                            </button>
                          </div>
                          <div className="alerts-publish__video-preview-player">
                            {this._renderVideo({
                              embedUrl: videoAtt.mediaId,
                            })}
                          </div>
                        </div>
                      )}
                    {this.state.videoUpload?.error ? (
                      <p className="alerts-publish__error">
                        {this.state.videoUpload.error}
                      </p>
                    ) : null}
                  </div>

                  {this.state.alertType && (
                    <div className="alerts-publish__trade">
                      <div className="alerts-publish__trade-head">
                        <span className="alerts-publish__trade-icon" aria-hidden="true">
                          <i className="material-icons">gps_fixed</i>
                        </span>
                        <span>
                          {this.state.updateMode
                            ? "Revise levels"
                            : "Trade levels"}
                        </span>
                      </div>
                      <div
                        className={`alerts-publish__trade-grid${
                          this.state.updateMode
                            ? " alerts-publish__trade-grid--update"
                            : ""
                        }`}
                      >
                        {!this.state.updateMode && (
                          <FormGroup>
                            <label htmlFor="alert-ticker">Ticker</label>
                            <ToolbarSelect
                              id="alert-ticker"
                              className={`alerts-publish__ticker${
                                !tickerValue
                                  ? " toolbar-select--placeholder"
                                  : ""
                              }`}
                              aria-label="Ticker"
                              value={
                                tickerValue === "" || tickerValue == null
                                  ? ""
                                  : String(tickerValue)
                              }
                              triggerLabel={
                                !tickerValue ? "Choose..." : undefined
                              }
                              onChange={(next) =>
                                this.setState({ selectedUnit: next || null })
                              }
                              options={[
                                { value: "", label: "Choose..." },
                                ...units.map((unit) => ({
                                  value: String(unit.id),
                                  label: unit.abbreviation,
                                })),
                              ]}
                            />
                          </FormGroup>
                        )}
                        {!this.state.updateMode && (
                          <FormGroup>
                            <label>Direction</label>
                            <div className="alerts-publish__sides">
                              <button
                                type="button"
                                className={`alerts-publish__side${
                                  isLong ? " is-long" : ""
                                }`}
                                aria-pressed={isLong}
                                onClick={() =>
                                  this.setState({ selectedDurability: 2 })
                                }
                              >
                                Long
                              </button>
                              <button
                                type="button"
                                className={`alerts-publish__side${
                                  !isLong ? " is-short" : ""
                                }`}
                                aria-pressed={!isLong}
                                onClick={() =>
                                  this.setState({ selectedDurability: 1 })
                                }
                              >
                                Short
                              </button>
                            </div>
                          </FormGroup>
                        )}
                        <FormGroup>
                          <label htmlFor="alert-entry">Entry</label>
                          <FormInput
                            id="alert-entry"
                            placeholder="0"
                            value={this.state.entry}
                            onChange={(text) =>
                              this.setState({ entry: text.target.value })
                            }
                          />
                        </FormGroup>
                        <FormGroup>
                          <label htmlFor="alert-target">Target</label>
                          <FormInput
                            id="alert-target"
                            placeholder="0"
                            value={this.state.target}
                            onChange={(text) =>
                              this.setState({ target: text.target.value })
                            }
                          />
                        </FormGroup>
                        <FormGroup
                          className={
                            this.state.updateMode
                              ? undefined
                              : "alerts-publish__stop"
                          }
                        >
                          <label htmlFor="alert-stop">Stop loss</label>
                          <FormInput
                            id="alert-stop"
                            placeholder="0"
                            value={this.state.stop}
                            onChange={(text) =>
                              this.setState({ stop: text.target.value })
                            }
                          />
                        </FormGroup>
                      </div>
                    </div>
                  )}

                  {this.state.showError && (
                    <p className="alerts-publish__error">{this.state.errorText}</p>
                  )}

                  <div className="alerts-publish__footer">
                    <Button
                      theme="accent"
                      className="alerts-publish__btn-publish btn-orange"
                      onClick={this.publish}
                      disabled={videoUploading}
                    >
                      {this.state.updateMode ? "Publish update" : "Publish alert"}
                    </Button>
                    <Button
                      theme=""
                      className="alerts-publish__btn-clear"
                      onClick={
                        this.state.updateMode
                          ? this.clear
                          : this.resetComposerFields
                      }
                    >
                      Clear
                    </Button>
                    {this.state.updateMode &&
                      this.state.updateAlert &&
                      !this.state.updateAlert.isInformational && (
                        <Button
                          theme=""
                          className="alerts-publish__close-trade"
                          onClick={this.closeTrade}
                          disabled={videoUploading}
                        >
                          Close trade
                        </Button>
                      )}
                  </div>
                </div>
              </CardBody>
            </Card>
            </div>
            );
          })()}
          <div className="alerts-lovable__feed-wrap">
            <div className="alerts-lovable__feed">
              {!this.state.user && (
                <div className="alerts-lovable__guest-banner">
                  <div className="alerts-lovable__guest-text">
                    <p>Member alerts</p>
                    <p>
                      Browse analyst updates below. Sign in or view plans for
                      full alert access.
                    </p>
                  </div>
                  <div className="alerts-lovable__guest-actions">
                    <Link to="/login" className="alerts-lovable__outline-btn">
                      Sign in
                    </Link>
                    <Link to="/prices" className="alerts-lovable__primary-btn">
                      View plans
                    </Link>
                  </div>
                </div>
              )}

              <div className="alerts-lovable__filter-card">
                <div className="alerts-lovable__filter-row">
                  <div className="alerts-lovable__avatars">
                    <button
                      type="button"
                      title="All analysts"
                      aria-label="All analysts"
                      aria-pressed={!this.state.publisherSelected}
                      className={
                        !this.state.publisherSelected
                          ? "alerts-lovable__avatar-btn alerts-lovable__avatar-btn--all is-selected"
                          : "alerts-lovable__avatar-btn alerts-lovable__avatar-btn--all"
                      }
                      onClick={(e) => {
                        e.preventDefault();
                        if (this.state.publisherSelected) {
                          this.selectPublisher(e, this.state.publisherSelected);
                        }
                      }}
                    >
                      All
                    </button>
                    {(this.state.publishers || [])
                      .filter((p) => p.isHidden === false)
                      .map((publisher) => (
                        <button
                          type="button"
                          key={publisher.id}
                          title={publisher.name}
                          aria-label={`Filter by ${publisher.name}`}
                          aria-pressed={
                            this.state.publisherSelected === publisher.id
                          }
                          className={
                            this.state.publisherSelected === publisher.id
                              ? "alerts-lovable__avatar-btn is-selected"
                              : "alerts-lovable__avatar-btn"
                          }
                          onClick={(e) => this.selectPublisher(e, publisher.id)}
                        >
                          <img
                            src={getPublisherAvatarUrl(publisher)}
                            alt=""
                          />
                        </button>
                      ))}
                  </div>

                  <div className="alerts-lovable__filter-actions">
                    {this.state.user && (
                      <div className="alerts-lovable__filter-topic">
                        <ToolbarSelect
                          id="feInputState"
                          className={
                            (Array.isArray(this.state.topicsSelected)
                              ? this.state.topicsSelected[0] || "*"
                              : this.state.topicsSelected || "*") === "*"
                              ? "toolbar-select--placeholder"
                              : ""
                          }
                          value={
                            Array.isArray(this.state.topicsSelected)
                              ? this.state.topicsSelected[0] || "*"
                              : this.state.topicsSelected || "*"
                          }
                          triggerLabel={
                            (Array.isArray(this.state.topicsSelected)
                              ? this.state.topicsSelected[0] || "*"
                              : this.state.topicsSelected || "*") === "*"
                              ? "Filter"
                              : undefined
                          }
                          onChange={this.changeAlertFilter}
                          options={topicsType.map((topic) => ({
                            value: topic.key,
                            label: topic.name,
                          }))}
                          aria-label="Filter alerts by type"
                        />
                      </div>
                    )}
                    {hasActiveFilters && (
                      <button
                        type="button"
                        className="alerts-lovable__clear-btn"
                        onClick={this.clearFilters}
                        aria-label="Clear filters"
                      >
                        <span className="material-icons" aria-hidden="true">
                          close
                        </span>
                        Clear filters
                      </button>
                    )}
                  </div>
                </div>
              </div>

            <div className="alerts-lovable__list">
              {!hasAlerts ? (
                <FilterEmptyState
                  variant="feed"
                  title="No alerts found"
                  message={
                    hasActiveFilters
                      ? "Nothing matches your current topic or analyst filters. Try another option, or use Clear filters above."
                      : "There are no alerts to show right now. Check back soon for analyst updates."
                  }
                  showClearAction={false}
                />
              ) : (
                this.state.alerts.map((alert) => (
                  <article
                    key={alert.id}
                    id={`alert-card-${alert.id}`}
                    className="alerts-feed-card"
                  >
                    <div className="alerts-feed-card__row">
                      <Link
                        to={`/analyst/${alert.publisher.slug}`}
                        className="alerts-feed-card__avatar-link"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <img
                          className="alerts-feed-card__avatar"
                          src={getPublisherAvatarUrl(alert.publisher)}
                          alt=""
                          loading="lazy"
                        />
                      </Link>

                      <div className="alerts-feed-card__main">
                        <div className="alerts-feed-card__topline">
                          <div className="alerts-feed-card__identity">
                            <Link
                              to={`/analyst/${alert.publisher.slug}`}
                              className="alerts-feed-card__name"
                            >
                              {alert.publisher.name}
                            </Link>
                            <span
                              className="alerts-feed-card__verified"
                              aria-label="Verified"
                              title="Verified"
                            >
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                aria-hidden="true"
                              >
                                <path
                                  d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="m9 12 2 2 4-4"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </span>
                            <span className="alerts-feed-card__meta-text alerts-feed-card__meta-text--topic">
                              {getAlertTopicLabel(alert)}
                            </span>
                            {alert.publisher.isFeaturedContributor === true && (
                              <>
                                <span className="alerts-feed-card__dot" aria-hidden="true">
                                  ·
                                </span>
                                <span className="alerts-feed-card__meta-text">
                                  Featured
                                </span>
                              </>
                            )}
                            {(alert.isInformational ||
                              alert?.alertStatusId === 1 ||
                              alert?.alertStatusId === 2 ||
                              alert?.createdAt) && (
                              <>
                                <span className="alerts-feed-card__dot" aria-hidden="true">
                                  ·
                                </span>
                                {this.renderAlertTimestamp(alert.createdAt)}
                              </>
                            )}
                          </div>

                          <div className="alerts-feed-card__topline-end">
                            {alert.tradingUnit?.abbreviation && (
                              <span className="alerts-feed-card__ticker">
                                ${alert.tradingUnit.abbreviation}
                              </span>
                            )}
                            {this.state.user &&
                              alert.publisher.id === this.state.user.id &&
                              alert.alertStatusId !== 2 && (
                                <button
                                  type="button"
                                  className="alerts-feed-card__edit"
                                  onClick={() => this.updateAlert(alert)}
                                  aria-label="Update alert"
                                >
                                  <i className="material-icons">edit</i>
                                </button>
                              )}
                          </div>
                        </div>

                        {!this.state.user ? (
                          <div className="alerts-lovable__gate">
                            <span
                              className="material-icons alerts-lovable__gate-icon"
                              aria-hidden="true"
                            >
                              lock
                            </span>
                            <p className="alerts-lovable__gate-title">
                              Members only
                            </p>
                            <p className="alerts-lovable__gate-subtitle">
                              Sign in with your membership to read this alert,
                              its charts and trade levels.
                            </p>
                            <div className="alerts-lovable__gate-actions">
                              <Link
                                to="/login"
                                className="alerts-lovable__primary-btn"
                              >
                                Sign in
                              </Link>
                              <Link
                                to="/prices"
                                className="alerts-lovable__outline-btn"
                              >
                                View plans
                              </Link>
                            </div>
                          </div>
                        ) : !alert.content ? (
                          <div className="alerts-lovable__gate">
                            <span
                              className="material-icons alerts-lovable__gate-icon"
                              aria-hidden="true"
                            >
                              lock
                            </span>
                            <p className="alerts-lovable__gate-title">
                              Members only
                            </p>
                            <p className="alerts-lovable__gate-subtitle">
                              Your membership has expired. Renew to unlock
                              alerts, charts and trade levels again.
                            </p>
                            <div className="alerts-lovable__gate-actions">
                              <Link
                                to="/user-profile"
                                className="alerts-lovable__primary-btn"
                              >
                                Renew membership
                              </Link>
                              <Link
                                to="/prices"
                                className="alerts-lovable__outline-btn"
                              >
                                View plans
                              </Link>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="alerts-feed-card__body alert-body">
                              <div
                                className="alerts-feed-card__content comment-text"
                                dangerouslySetInnerHTML={{
                                  __html: alert.content,
                                }}
                              />

                              {alert.attachments &&
                                alert.attachments.length > 0 && (
                                  <div className="alerts-feed-card__media">
                                    <div className="gallery-wrapper">
                                      <ImageGallery
                                        showPlayButton={false}
                                        items={alert.attachments.map(
                                          this.mapAttachments
                                        )}
                                      />
                                    </div>
                                  </div>
                                )}

                              {alert.tradingUnit && !alert.entry && (
                                <div className="alerts-feed-card__chips">
                                  <span className="alerts-feed-card__chip alerts-feed-card__chip--unit">
                                    {`${alert.tradingUnit.abbreviation} · ${alert.tradingUnit.name}`}
                                  </span>
                                </div>
                              )}

                              {alert.entry && (
                                <div className="alerts-feed-card__levels-card">
                                  <div className="alerts-feed-card__levels-head">
                                    <span
                                      className="material-icons"
                                      aria-hidden="true"
                                    >
                                      gps_fixed
                                    </span>
                                    <span>Trade levels</span>
                                    {(alert.alertDurabilityId === 1 ||
                                      alert.alertDurabilityId === 2 ||
                                      alert.alertStatusId === 1 ||
                                      alert.alertStatusId === 2) && (
                                      <span
                                        className={`alerts-feed-card__levels-badge${
                                          alert.alertDurabilityId === 1
                                            ? " alerts-feed-card__levels-badge--short"
                                            : alert.alertDurabilityId === 2
                                              ? " alerts-feed-card__levels-badge--long"
                                              : ""
                                        }`}
                                      >
                                        {[
                                          alert.alertDurabilityId === 1
                                            ? "Short"
                                            : alert.alertDurabilityId === 2
                                              ? "Long"
                                              : null,
                                          alert.alertStatusId === 1
                                            ? "Open"
                                            : alert.alertStatusId === 2
                                              ? "Closed"
                                              : null,
                                        ]
                                          .filter(Boolean)
                                          .join(" · ")}
                                      </span>
                                    )}
                                  </div>
                                  {alert.tradingUnit && (
                                    <div className="alerts-feed-card__chips">
                                      <span className="alerts-feed-card__chip alerts-feed-card__chip--unit">
                                        {`${alert.tradingUnit.abbreviation} · ${alert.tradingUnit.name}`}
                                      </span>
                                    </div>
                                  )}
                                  <div className="alerts-feed-card__levels">
                                    <div className="alerts-feed-card__level">
                                      <span className="alerts-feed-card__level-label">
                                        Entry
                                      </span>
                                      <span className="alerts-feed-card__level-value">
                                        {alert.entry}
                                      </span>
                                    </div>
                                    <div className="alerts-feed-card__level">
                                      <span className="alerts-feed-card__level-label">
                                        Target
                                      </span>
                                      <span className="alerts-feed-card__level-value">
                                        {alert.target}
                                      </span>
                                    </div>
                                    <div className="alerts-feed-card__level">
                                      <span className="alerts-feed-card__level-label">
                                        Stop loss
                                      </span>
                                      <span className="alerts-feed-card__level-value">
                                        {alert.stopLoss}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>

                            <div className="alerts-feed-card__footer">
                              <button
                                type="button"
                                className={`alerts-feed-card__icon-action${
                                  alert.tracking
                                    ? " alerts-feed-card__icon-action--active"
                                    : ""
                                }`}
                                onClick={() => this.trackTrade(alert)}
                              >
                                <span
                                  className="material-icons"
                                  aria-hidden="true"
                                >
                                  {alert.tracking
                                    ? "bookmark"
                                    : "bookmark_border"}
                                </span>
                                <span>
                                  {alert.tracking ? "Tracking" : "Track"}
                                </span>
                              </button>
                              <button
                                type="button"
                                className={`alerts-feed-card__icon-action${
                                  alert.isFavourite
                                    ? " alerts-feed-card__icon-action--active"
                                    : ""
                                }`}
                                onClick={() => this.createFavoriteAlert(alert)}
                              >
                                <span
                                  className="material-icons"
                                  aria-hidden="true"
                                >
                                  {alert.isFavourite ? "star" : "star_border"}
                                </span>
                                <span>Favorite</span>
                              </button>
                              <SharePopover
                                className="alerts-feed-card__icon-action alerts-feed-card__share"
                                url={
                                  window.location.origin +
                                  "/alerts/" +
                                  alert.id +
                                  "?aid=" +
                                  alert.publisher.affiliateId
                                }
                                show={alert.showShare}
                                onToggle={() => this.showShare(alert)}
                              />
                            </div>

                            {alert.updates && alert.updates.length > 0 && (
                              <div className="alerts-feed-card__thread-toggle">
                                <button
                                  type="button"
                                  className="alerts-feed-card__thread-btn"
                                  onClick={() => this.toggleUpdates(alert)}
                                >
                                  {alert.updatesOpen
                                    ? "Hide thread"
                                    : `Show thread (${alert.updates.length})`}
                                  <i
                                    className="material-icons"
                                    aria-hidden="true"
                                  >
                                    {alert.updatesOpen
                                      ? "expand_less"
                                      : "expand_more"}
                                  </i>
                                </button>
                              </div>
                            )}

                            {alert.updates && alert.updatesOpen && (
                              <div
                                className="alerts-feed-card__thread"
                                id={`alert-thread-${alert.id}`}
                              >
                                {alert.updates.map((update, updateIdx) => (
                                  <div
                                    key={`${alert.id}-update-${updateIdx}`}
                                    className="alerts-feed-card__update"
                                  >
                                    <p className="alerts-feed-card__update-date">
                                      Update
                                      {update.createdAt
                                        ? ` · ${formatAlertDay(update.createdAt)}`
                                        : ""}
                                    </p>
                                    <div
                                      className="alerts-feed-card__content comment-text"
                                      dangerouslySetInnerHTML={{
                                        __html: update.content,
                                      }}
                                    />
                                    {update.attachments &&
                                      update.attachments.length > 0 && (
                                        <div className="alerts-feed-card__media">
                                          <div className="gallery-wrapper">
                                            <ImageGallery
                                              showPlayButton={false}
                                              items={update.attachments.map(
                                                this.mapAttachments
                                              )}
                                            />
                                          </div>
                                        </div>
                                      )}
                                    {update.entry && (
                                      <div className="alerts-feed-card__levels-card">
                                        <div className="alerts-feed-card__levels-head">
                                          <span
                                            className="material-icons"
                                            aria-hidden="true"
                                          >
                                            gps_fixed
                                          </span>
                                          <span>Trade levels</span>
                                        </div>
                                        <div className="alerts-feed-card__levels">
                                          <div className="alerts-feed-card__level">
                                            <span className="alerts-feed-card__level-label">
                                              Entry
                                            </span>
                                            <span className="alerts-feed-card__level-value">
                                              {update.entry}
                                            </span>
                                          </div>
                                          <div className="alerts-feed-card__level">
                                            <span className="alerts-feed-card__level-label">
                                              Target
                                            </span>
                                            <span className="alerts-feed-card__level-value">
                                              {update.target}
                                            </span>
                                          </div>
                                          <div className="alerts-feed-card__level">
                                            <span className="alerts-feed-card__level-label">
                                              Stop loss
                                            </span>
                                            <span className="alerts-feed-card__level-value">
                                              {update.stopLoss}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </article>
                ))
              )}
            </div>
          </div>
        </div>
        {this.state.expandedImage && (
          <div
            className="alerts-feed-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label="Expanded chart"
            onClick={() => this.setState({ expandedImage: null })}
          >
            <button
              type="button"
              className="alerts-feed-lightbox__close"
              aria-label="Close"
              onClick={() => this.setState({ expandedImage: null })}
            >
              <span className="material-icons" aria-hidden="true">
                close
              </span>
            </button>
            <img
              src={this.state.expandedImage}
              alt=""
              onClick={(event) => event.stopPropagation()}
            />
          </div>
        )}
      </div>
    );
  }
}

export default withRouter(Alerts);
